import { httpClient } from "@/app/client/httpClient";
import { authStore } from "@/store/auth";
import { AxiosError } from "axios";

class AuthService {
  async login(body: { email: string; password: string }) {
    const authInfo = await httpClient.auth.login.$post({
      body
    });

    await authStore.login(authInfo);
  }

  /**
   * tokenをリフレッシュします
   * @returns {boolean} トークンリフレッシュが成功したかどうか
   */
  async refreshToken(): Promise<boolean> {
    const token = authStore.token;
    if (!token) {
      await authStore.logout();
      return false;
    }

    return (
      httpClient.auth.token.refresh
        .$post({
          body: {
            token
          }
        })
        .then(({ token }) => {
          authStore.refresh({
            token
          });
          return true;
        })
        // eslint-disable-next-line
        .catch((err: AxiosError) => {
          console.log("cant refresh... login again");

          const tokenLifetime = authStore.tokenLifetime();

          // refresh 失敗したとき, 有効期限切れトークンをもつ場合はトークンクリアする
          if (tokenLifetime === null || tokenLifetime <= 0) {
            authStore.logout();
          }
          return false;
        })
    );
  }

  resetPassword(body: { email: string }) {
    return httpClient.auth.password.reset.$post({
      body
    });
  }

  passwordResetConfirm(body: {
    newPassword1: string;
    newPassword2: string;
    uid: string;
    token: string;
  }) {
    return httpClient.auth.password.reset.confirm.$post({ body });
  }

  changePassword(body: {
    oldPassword: string;
    newPassword1: string;
    newPassword2: string;
  }) {
    return httpClient.auth.password.change.$post({ body }).then(() => {
      const user = authStore.user;
      const hasChangedInitialPassword = user?.hasChangedInitialPassword;
      if (!hasChangedInitialPassword) {
        const updatedUser = Object.assign({}, user);
        updatedUser.hasChangedInitialPassword = true;
        authStore.changeUserInfo(updatedUser);
      }
      return true;
    });
  }

  /**
   * v1ユーザーを認証する
   * 認証後問題なければログインさせる
   * @param body
   */
  async authenticateV1User(body: {
    email: string;
    phoneNumber: string;
    firstName: string;
    lastName: string;
    firstNameKana: string;
    lastNameKana: string;
    birthDate: string;
    serviceName: string; // NOTE: 削除したいがv1ユーザ向けなので放置
  }) {
    await httpClient.auth.worker.authentication.$post({ body });
  }

  async workerLogin(body: { password: string; phoneNumber: string }) {
    const authInfo = await httpClient.auth.worker.login.$post({ body });
    authStore.login(authInfo);
    authStore.clearRegisterInfo();
  }

  requestWorkerLoginStatus(body: { phoneNumber: string }) {
    return httpClient.auth.worker.login_status.$post({ body });
  }

  async registration(body: { smsSessionId: string; password: string }) {
    const authInfo = await httpClient.auth.registration.$post({ body });
    authStore.login(authInfo);
    authStore.clearRegisterInfo();
  }

  requestWorkerPasswordReset(body: { smsSessionId: string; password: string }) {
    return httpClient.auth.worker.password_reset.$post({ body });
  }

  async getImmediatelyExpiryToken() {
    return await httpClient.auth.immediately_expiry_tokens.$post();
  }

  verifyReactivateWorkers(body: { smsSessionId: string; birthDate: string }) {
    return httpClient.auth.reactivate_workers.verify.$post({ body });
  }

  async reactivateWorker(body: { smsSessionId: string; password: string }) {
    const authInfo = await httpClient.auth.reactivate_workers.$post({ body });
    authStore.login(authInfo);
    authStore.clearRegisterInfo();
  }

  async fetchAccountStatus(
    phoneNumber: string
  ): Promise<"ban" | "requiredPhoneNumberAuth" | "requiredLogin"> {
    const {
      status,
      isActive,
      isBan
    } = await authService.requestWorkerLoginStatus({
      phoneNumber
    });
    authStore.changePhoneNumber(phoneNumber);
    authStore.changeWorkerLoginStatus(status);

    // NOTE: 現状、ワーカーの状態は以下のように判断できる
    // isActive=true/isLeave=false: アクティブワーカー
    // isActive=false/isLeave=true: 退会済みワーカー（認証済 or 未認証）
    // isActive=false/isLeave=false/status=[login_user|v1_user]: 停止(BAN)済みワーカー

    // 電話番号認証が必要なユーザー
    if (
      ["new_user", "verified_leave_user", "unverified_leave_user"].includes(
        status
      )
    ) {
      return "requiredPhoneNumberAuth";
    }

    if (["login_user", "v1_user"].includes(status)) {
      // 停止済みユーザー
      if (isBan) {
        return "ban";
      }
      // 無効ユーザー
      // 基本的に非退会ユーザは isActive = false の場合は、isBan = true になっているはずだが、
      // どこかの処理で isBan を更新していない可能性も考慮して isActive = false の場合は ban とする
      if (!isActive) {
        return "ban";
      }
      // アクティブな登録済みユーザー
      return "requiredLogin";
    }

    // 上記以外のユーザーはエラー扱いとする（原則起こらないはず）
    throw new Error("不正なユーザー");
  }
}

export const authService = new AuthService();
